import { memo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

import { COLORS, CORAL_LAYOUT } from "./dioramaLayout";
import type { CoralType } from "./dioramaLayout";

/** Bioluminescent emissive color per daytime palette key (night mode only). */
const BIOLUM_COLORS: Partial<Record<keyof typeof COLORS, string>> = {
  coral1: "#FF5FA8", // warm magenta
  coral2: "#3FE8D8", // cyan-teal
  coral3: "#57F2C8", // sea-green
  pink: "#FF66C4", // magenta
  teal: "#3FE8FF", // cyan
  purple: "#B47CFF", // violet
};
const BIOLUM_FALLBACK = "#3FE8FF";

interface CoralProps {
  color: string;
  /** When set, the coral glows this color (dark-mode bioluminescence). */
  glowColor?: string;
}

function BranchCoral({ color, glowColor }: CoralProps) {
  const branches = [0, 1, 2, 3].map((i) => {
    const angle = (i / 4) * Math.PI * 2 + i * 0.3;
    const h = 0.6 + ((Math.sin(i * 7.13) + 1) / 2) * 0.8;
    return {
      key: i,
      position: [
        Math.cos(angle) * 0.15,
        h / 2,
        Math.sin(angle) * 0.15,
      ] as [number, number, number],
      tipPosition: [
        Math.cos(angle) * 0.15,
        h + 0.03,
        Math.sin(angle) * 0.15,
      ] as [number, number, number],
      h,
      rotX: (Math.sin(i * 3.7) - 0.5) * 0.4,
      rotZ: (Math.cos(i * 5.1) - 0.5) * 0.4,
    };
  });

  return (
    <>
      {branches.map((b) => (
        <group key={b.key}>
          <mesh position={b.position} rotation={[b.rotX, 0, b.rotZ]} castShadow receiveShadow>
            <cylinderGeometry args={[0.04, 0.08, b.h, 5]} />
            <meshStandardMaterial
              color={color}
              flatShading
              emissive={glowColor ?? "#000000"}
              emissiveIntensity={glowColor ? 0.25 : 0}
            />
          </mesh>
          <mesh position={b.tipPosition}>
            <sphereGeometry args={[0.06, 5, 4]} />
            <meshStandardMaterial
              color={color}
              flatShading
              emissive={glowColor ?? color}
              emissiveIntensity={glowColor ? 1.1 : 0.15}
            />
          </mesh>
        </group>
      ))}
    </>
  );
}

function FanCoral({ color, glowColor }: CoralProps) {
  const pieces = [0, 1, 2, 3, 4].map((i) => ({
    key: i,
    h: 0.4 + i * 0.2,
    w: 0.8 - Math.abs(i - 2) * 0.15,
  }));

  return (
    <>
      {/* Stem */}
      <mesh position={[0, 0.2, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[0.04, 0.06, 0.4, 5]} />
        <meshStandardMaterial
          color={color}
          flatShading
          emissive={glowColor ?? "#000000"}
          emissiveIntensity={glowColor ? 0.2 : 0}
        />
      </mesh>
      {/* Fan layers */}
      {pieces.map((p) => (
        <mesh key={p.key} position={[0, p.h, 0]} castShadow receiveShadow>
          <boxGeometry args={[p.w, 0.06, 0.04]} />
          <meshStandardMaterial
            color={color}
            flatShading
            emissive={glowColor ?? "#000000"}
            emissiveIntensity={glowColor ? 0.5 : 0}
          />
        </mesh>
      ))}
    </>
  );
}

function BlobCoral({ color, glowColor }: CoralProps) {
  const minis = [0, 1, 2].map((i) => {
    const a = (i / 3) * Math.PI * 2;
    return {
      key: i,
      position: [Math.cos(a) * 0.3, 0.15, Math.sin(a) * 0.3] as [number, number, number],
    };
  });

  return (
    <>
      <mesh position={[0, 0.3, 0]} scale={[1, 0.7, 1]} castShadow receiveShadow>
        <dodecahedronGeometry args={[0.3, 0]} />
        <meshStandardMaterial
          color={color}
          flatShading
          emissive={glowColor ?? color}
          emissiveIntensity={glowColor ? 0.7 : 0.1}
        />
      </mesh>
      {minis.map((m) => (
        <mesh key={m.key} position={m.position} receiveShadow>
          <dodecahedronGeometry args={[0.12, 0]} />
          <meshStandardMaterial
            color={color}
            flatShading
            emissive={glowColor ?? color}
            emissiveIntensity={glowColor ? 0.55 : 0.1}
          />
        </mesh>
      ))}
    </>
  );
}

function TubeCoral({ color, glowColor }: CoralProps) {
  const tubes = [0, 1, 2, 3, 4].map((i) => {
    const h = 0.5 + ((Math.sin(i * 11.3) + 1) / 2) * 0.6;
    const a = (i / 5) * Math.PI * 2;
    return {
      key: i,
      position: [Math.cos(a) * 0.15, h / 2, Math.sin(a) * 0.15] as [number, number, number],
      capPosition: [Math.cos(a) * 0.15, h, Math.sin(a) * 0.15] as [number, number, number],
      h,
    };
  });

  return (
    <>
      {tubes.map((t) => (
        <group key={t.key}>
          <mesh position={t.position} castShadow receiveShadow>
            <cylinderGeometry args={[0.05, 0.07, t.h, 6]} />
            <meshStandardMaterial
              color={color}
              flatShading
              emissive={glowColor ?? "#000000"}
              emissiveIntensity={glowColor ? 0.25 : 0}
            />
          </mesh>
          <mesh position={t.capPosition}>
            <sphereGeometry args={[0.06, 4, 3]} />
            <meshStandardMaterial
              color={color}
              flatShading
              emissive={glowColor ?? color}
              emissiveIntensity={glowColor ? 1.2 : 0.2}
            />
          </mesh>
        </group>
      ))}
    </>
  );
}

const CORAL_COMPONENTS: Record<CoralType, React.FC<CoralProps>> = {
  branch: BranchCoral,
  fan: FanCoral,
  blob: BlobCoral,
  tube: TubeCoral,
};

function ProceduralCoralsInner({ isNight = false }: { isNight?: boolean }) {
  const groupRefs = useRef<(THREE.Group | null)[]>(
    new Array(CORAL_LAYOUT.length).fill(null),
  );

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    for (let i = 0; i < CORAL_LAYOUT.length; i++) {
      const group = groupRefs.current[i];
      if (!group) continue;
      const entry = CORAL_LAYOUT[i];
      group.rotation.z = Math.sin(t * 0.5 + entry.position[0]) * 0.03;
      group.rotation.x = Math.cos(t * 0.4 + entry.position[2]) * 0.02;
    }
  });

  return (
    <>
      {CORAL_LAYOUT.map((entry, i) => {
        const CoralComponent = CORAL_COMPONENTS[entry.type];
        const color = COLORS[entry.color];
        const glowColor = isNight
          ? BIOLUM_COLORS[entry.color] ?? BIOLUM_FALLBACK
          : undefined;
        return (
          <group
            key={i}
            ref={(el) => { groupRefs.current[i] = el; }}
            position={entry.position}
            scale={[entry.scale, entry.scale, entry.scale]}
          >
            <CoralComponent color={color} glowColor={glowColor} />
          </group>
        );
      })}
    </>
  );
}

export const ProceduralCorals = memo(ProceduralCoralsInner);
